import type { ShiftInviteStatus } from "@clipboard-health/contract-backend-main";
import { isDefined } from "@clipboard-health/util-ts";

import { APP_V2_APP_EVENTS, logError } from "../../../lib/analytics";

interface ShiftInviteMessageData {
  shiftInviteId: string | undefined;
  status: ShiftInviteStatus | undefined;
}

/**
 * Check if a string is valid JSON
 */
function isValidJson(value: string): boolean {
  try {
    JSON.parse(value);
    return true;
  } catch {
    return false;
  }
}

/**
 * Parse shift invite data from the admin message data field.
 * Returns shiftInviteId and status if present and valid.
 */
export function parseShiftInviteMessageData(data: string | undefined): ShiftInviteMessageData {
  const defaultData: ShiftInviteMessageData = {
    shiftInviteId: undefined,
    status: undefined,
  };

  if (!isDefined(data) || data === "") {
    return defaultData;
  }

  if (!isValidJson(data)) {
    return defaultData;
  }

  try {
    const parsed = JSON.parse(data) as {
      shiftInviteId?: string;
      status?: ShiftInviteStatus;
    };

    return {
      shiftInviteId: parsed.shiftInviteId,
      status: parsed.status,
    };
  } catch {
    // Silently return default data for parse failures
    return defaultData;
  }
}

/**
 * Parse the number of invites from a group shift invite message.
 * Tries to extract from the data field first, then falls back to regex matching on the message.
 */
export function parseGroupShiftInviteCount(
  data: string | undefined,
  message: string
): number | undefined {
  // Try to extract from data field first
  if (isDefined(data) && isValidJson(data)) {
    try {
      const parsed = JSON.parse(data) as { numberOfInvites?: number };
      if (isDefined(parsed.numberOfInvites)) {
        return parsed.numberOfInvites;
      }
    } catch {
      // Fall through to regex extraction
    }
  }

  // Extract number of invites from message content as fallback
  const match = /You sent (\d+)\s+shift invites?/.exec(message);
  if (isDefined(match)) {
    return Number(match[1]);
  }

  logError(APP_V2_APP_EVENTS.EXTRACT_GROUP_SHIFT_INVITES_QUANTITY_FAILURE, {
    error: new Error("Failed to extract number of shift invites from message"),
    metadata: { data, message },
  });

  return undefined;
}
